import { useState, useEffect } from "react";
import { useApi } from "../utils/Api";
import Translate from "../utils/Translate";
import { useSearchParams } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { CardContent, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import BankIdAuthentication from "../components/bankId/BankIdAuthentication";
import Preloader from "../components/Preloader";
import Card from "@mui/material/Card";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { GridCheckCircleIcon } from "@mui/x-data-grid";

const Privacy = (props) => {
  const [searchParams] = useSearchParams();
  const api = useApi();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [showView, setShowView] = useState(isSmallScreen ? "bankId" : "init");
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [langLoaded, setLangLoaded] = useState(false);

  const [name, setName] = useState("");
  const [orgNo, setOrgNo] = useState("");
  const [email, setEmail] = useState("");
  const [deleteData, setDeleteData] = useState(false);
  const [copyData, setCopyData] = useState(false);
  const [otherRequest, setOtherRequest] = useState(false);
  const [otherRequestText, setOtherRequestText] = useState("");

  useEffect(() => {
    const loadLang = async () => {
      const lang = searchParams.get("lang") ?? localStorage.lang ?? "29";
      await Translate.loadPreLogin(lang);
      setLangLoaded(true);
    };
    loadLang();
  }, [searchParams]);

  function disableSendButton() {
    if (name === "" || email === "") {
      return true;
    }

    if (otherRequest) {
      return otherRequestText.trim() === "";
    }

    if (!copyData && !deleteData) {
      return true;
    }

    return false;
  }

  function formatNames(nameString) {
    let words = nameString.split(" ");
    let capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

    return capitalizedWords.join(" ");
  }

  function setOtherRequestChecked(event) {
    setOtherRequest(event.target.checked);
    if (event.target.checked) {
      setDeleteData(false);
      setCopyData(false);
    } else {
      setOtherRequestText("");
    }
  }

  function onAuthenticationSuccess(loginType, response) {
    setShowView("form");

    setOrgNo(response.data.ssn);
    setToken(response.data.token);
    setName(formatNames(response.data.givenName) + " " + formatNames(response.data.surName));
  }

  function clearForm() {
    setName("");
    setOrgNo("");
    setEmail("");
    setDeleteData(false);
    setCopyData(false);
    setOtherRequest(false);
    setOtherRequestText("");
  }

  async function postForm() {
    setIsLoading(true);

    let reqObj = {
      name: name,
      orgNo: orgNo,
      email: email,
      token: token,
      removeData: deleteData,
      copyOfData: copyData,
      otherRequest: otherRequest,
      otherRequestText: otherRequestText,
    };

    try {
      let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}privacy`, reqObj, "POST", false, true);
      if (response.isSuccessful) {
        setShowView("success");
        clearForm();
      } else if (response.errorMessageTranslationKey === "Unauthorized") {
        setShowView("bankId");
        clearForm();
      }
    } catch (error) {
      setShowView("init");
    } finally {
      setIsLoading(false);
    }
  }

  if (!langLoaded) {
    return <Preloader />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        maxWidth: isSmallScreen ? "" : "80em",
        justifyContent: isSmallScreen ? "" : "center",
        margin: "0 auto",
        padding: "1em",
      }}
    >
      <div style={{ flex: 1 }}>
        {isLoading && <Preloader />}
        <Grid container spacing={2}>
          <Grid size={{ md: 12, xs: 12 }}>
            <img
              src={!isSmallScreen ? "/static/gdpr_desktop.png" : "/static/gdpr_mobile.png"}
              alt=""
              style={{
                maxHeight: "200px",
                width: "100%",
                objectFit: "contain",
              }}
            />
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <div style={{ maxWidth: "35em", margin: isSmallScreen ? "0 1em" : "0 auto" }}>
              <p style={{ fontWeight: "bold" }}>Personuppgifter hos MA-system Utbildning AB</p>
              <p>
                Var och en som har sina personuppgifter hos MA-system Utbildning AB har rätt att hämta dessa uppgifter
                via ett elektroniskt standardformat eller begära borttagning av informationen.
              </p>
              <br />
              <p>Om du vill göra detta börjar du med att legitimera dig med BankID och fyller därefter i formuläret.</p>
            </div>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} style={{ display: "flex", justifyContent: "center" }}>
            <div>
              {showView === "init" && (
                <Card style={{ width: "20em", height: "15em" }}>
                  <CardContent
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                    }}
                  >
                    <b>Logga in med BankID</b>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => setShowView("bankId")}>
                      Starta BankID
                    </Button>
                  </CardContent>
                </Card>
              )}

              {showView === "bankId" && (
                <div>
                  <BankIdAuthentication
                    startAuthenticationUrl={`${import.meta.env.VITE_MAIN_URL}privacy/bankId/authenticate`}
                    onAuthenticationSuccess={onAuthenticationSuccess}
                  />
                </div>
              )}

              {showView === "form" && (
                <Card style={{ padding: "1em", margin: isSmallScreen ? "0.4em" : "" }}>
                  <Grid container spacing={2}>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <b>Personuppgifter</b>
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }}>Personnummer: {orgNo}</Grid>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <TextField
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        label="Namn"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <TextField
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        label="Email"
                        variant="outlined"
                        fullWidth
                        required
                        inputProps={{ maxLength: 100 }}
                      />
                    </Grid>

                    <Grid size={{ md: 6, xs: 12 }}>
                      <br />
                      <p>Jag vill:</p>
                    </Grid>

                    <Grid size={{ md: 12, xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={deleteData}
                            onChange={(event) => setDeleteData(event.target.checked)}
                            disabled={otherRequest}
                          />
                        }
                        label="Bli borttagen från MA-systems tjänster."
                      />
                    </Grid>

                    <Grid size={{ md: 12, xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={copyData}
                            onChange={(event) => setCopyData(event.target.checked)}
                            disabled={otherRequest}
                          />
                        }
                        label="Få en kopia av min data."
                      />
                    </Grid>

                    <Grid size={{ md: 12, xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Checkbox checked={otherRequest} onChange={(event) => setOtherRequestChecked(event)} />
                        }
                        label="Annan förfrågan."
                      />

                      {otherRequest && (
                        <TextField
                          value={otherRequestText}
                          onChange={(event) => setOtherRequestText(event.target.value)}
                          label="Ange din förfrågan"
                          variant="outlined"
                          fullWidth
                          required
                          multiline
                          rows={4}
                          style={{ marginTop: "1em" }}
                          inputProps={{ maxLength: 250 }}
                        />
                      )}
                    </Grid>

                    <Grid size={{ md: 12, xs: 12 }}>
                      <Grid container justify="flex-end">
                        <Button variant="contained" onClick={postForm} disabled={disableSendButton()}>
                          Skicka
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              )}

              {showView === "success" && (
                <div style={{ paddingTop: "3em" }}>
                  <Card
                    sx={{
                      backgroundColor: "#f9fff9",
                      marginLeft: isSmallScreen ? "1em" : "",
                      marginRight: isSmallScreen ? "1em" : "",
                    }}
                  >
                    <CardContent sx={{ display: "flex", alignItems: "center" }}>
                      <GridCheckCircleIcon
                        color="success"
                        sx={{
                          fontSize: 40,
                          mr: 2,
                        }}
                      />
                      <div>
                        <Typography variant="h6">Skickad!</Typography>
                        <Typography>Tack för din förfrågan. Vi återkommer till dig.</Typography>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </div>

      <footer
        style={{
          marginTop: isSmallScreen ? "4em" : "1em",
          padding: "1em",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "left",
          backgroundColor: "#f9f9f9",
          borderTop: "1px solid #ddd",
          fontSize: "0.9em",
          color: "#555",
        }}
      >
        <p style={{ margin: 0, maxWidth: "90%" }}>
          Detta är en tjänst som administreras av MA-system Utbildning AB. När du skickar en förfrågan via detta
          formulär godkänner du samtidigt att vi får behandla dina personuppgifter för att kunna utföra åtgärden. Om du
          ej har tillgång till BankId, kontakta oss på <a href="mailto:education@masystem.se">education@masystem.se</a>
        </p>
      </footer>
    </div>
  );
};
export default Privacy;
